import * as THREE from '@phdsoft/three';

/**
 * Fraction of the camera's distance to the light model's world bounding box used as the camera
 * near plane for the light model in MODEL navigation. Every point of the light model lies at
 * least that distance from the camera, so half of it can never clip the light model. At a
 * whole-ship view (camera 341 m from the orbit target) it moves the near plane from 0.5 m out to
 * ~124 m: with near 0.5 the 24-bit depth buffer has too little precision at 300-600 m and
 * GTAO, which reconstructs positions and normals from that depth, turns it into speckle
 * (UAT 2026-10-30).
 */
export const LIGHT_MODEL_NEAR_FRACTION = 0.5;

export interface LightModelCameraNearInput {
  /** Asset.mode: 'MODEL' | 'REGIONS' | 'SECTIONS' */
  navigationMode: string | null | undefined;
  /** Asset.isLightModel */
  isLightModel: boolean;
  /** Camera world position. Not modified. */
  cameraPosition: THREE.Vector3;
  /** World bounding box of asset.lightCampaign.group; null when not known yet. Not modified. */
  lightModelBox: THREE.Box3 | null;
  /**
   * OrbitControls.target; null when there are no controls. Not modified. Whatever the camera
   * orbits must never be clipped, even off the light model: the drone chase cam keeps it on the
   * drone, ~2 m from the camera, possibly tens of metres off the hull.
   */
  orbitTarget?: THREE.Vector3 | null;
  /** The camera's near everywhere else (CAMERA_DEFAULT_NEAR). */
  defaultNear: number;
}

function isFiniteVector(v: THREE.Vector3): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

/**
 * Camera near plane for this frame: for the light model in MODEL navigation,
 * max(defaultNear, LIGHT_MODEL_NEAR_FRACTION × distance from the camera to the light model box)
 * (the distance is 0 with the camera inside the box, giving defaultNear). A finite orbit target
 * nearer than the box takes its place, so the target is never clipped either. Everything else, a
 * missing / empty / non-finite box, a non-finite camera position or a non-finite result keeps
 * defaultNear. A pure function of the camera pose, so a still camera always gets the same near.
 */
export function lightModelCameraNear(input: LightModelCameraNearInput): number {
  const { defaultNear, lightModelBox, cameraPosition } = input;
  if (input.navigationMode !== 'MODEL' || !input.isLightModel) {
    return defaultNear;
  }
  if (!lightModelBox || lightModelBox.isEmpty() || !isFiniteVector(lightModelBox.min) || !isFiniteVector(lightModelBox.max)) {
    return defaultNear;
  }
  if (!cameraPosition || !isFiniteVector(cameraPosition)) {
    return defaultNear;
  }
  // was: const near = Math.max(defaultNear, LIGHT_MODEL_NEAR_FRACTION * lightModelBox.distanceToPoint(cameraPosition));
  let distance = lightModelBox.distanceToPoint(cameraPosition);
  if (input.orbitTarget && isFiniteVector(input.orbitTarget)) {
    distance = Math.min(distance, cameraPosition.distanceTo(input.orbitTarget));
  }
  const near = Math.max(defaultNear, LIGHT_MODEL_NEAR_FRACTION * distance);
  return Number.isFinite(near) ? near : defaultNear;
}
